<script setup lang="ts">
    import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
    import { useGlobalSearch } from '@/composables/useGlobalSearch';
    import { Input } from '@/components/ui/input';
    import { Card, CardContent } from '@/components/ui/card';
    import { LoaderCircle, Search, X } from 'lucide-vue-next';
    import GlobalSearchResult from '@/components/GlobalSearchResult.vue';
    import { useI18n } from 'vue-i18n';
    import { router } from '@inertiajs/vue3';
    import { route } from 'ziggy-js';

    const { t } = useI18n();
    const inputRef = ref<InstanceType<typeof Input>>();
    const resultsRef = ref<HTMLElement>();
    const isDropdownOpen = ref(false);
    const selectedIndex = ref(-1);

    const {
        searchQuery,
        searchResults,
        isLoading,
        isFetching,
        hasResults,
        isMinimumQuery,
        setSearchQuery,
        clearSearch,
    } = useGlobalSearch();

    // Computed property to check if result has a meaningful name
    const hasResultName = (result: any) => {
        return (
            result.name &&
            result.name.trim() !== '' &&
            result.name !== null &&
            result.name !== undefined
        );
    };

    // Helper function for highlighting text
    const highlightText = (text: string, query: string): string => {
        if (!query) return text;
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(
            regex,
            '<mark class="bg-primary/20 text-primary px-1 rounded">$1</mark>',
        );
    };

    // Handle input changes
    const handleInput = (event: Event) => {
        const target = event.target as HTMLInputElement;
        setSearchQuery(target.value);

        if (target.value.length >= 2) {
            isDropdownOpen.value = true;
            selectedIndex.value = -1;
        } else {
            isDropdownOpen.value = false;
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (event: KeyboardEvent) => {
        if (!isDropdownOpen.value || !hasResults.value) return;

        // Count total results including all contexts
        const totalResults =
            searchResults.value?.results?.reduce((count, result) => {
                return count + (result.contexts?.length || 1);
            }, 0) || 0;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                selectedIndex.value = Math.min(selectedIndex.value + 1, totalResults - 1);
                scrollToSelected();
                break;
            case 'ArrowUp':
                event.preventDefault();
                selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
                scrollToSelected();
                break;
            case 'Enter':
                event.preventDefault();
                if (selectedIndex.value >= 0) {
                    navigateToSelectedResult();
                }
                break;
            case 'Escape':
                event.preventDefault();
                closeDropdown();
                break;
        }
    };

    // Scroll to selected item
    const scrollToSelected = async () => {
        await nextTick();
        if (resultsRef.value && selectedIndex.value >= 0) {
            const allElements = resultsRef.value.querySelectorAll('a, .result-item');
            const selectedElement = allElements[selectedIndex.value] as HTMLElement;
            selectedElement?.scrollIntoView({ block: 'nearest' });
        }
    };

    // Navigate to selected result
    const navigateToSelectedResult = () => {
        if (!searchResults.value?.results) return;

        let currentIndex = 0;
        for (const result of searchResults.value.results) {
            if (result.contexts && result.contexts.length > 0) {
                for (const context of result.contexts) {
                    if (currentIndex === selectedIndex.value) {
                        navigateToResult(context);
                        return;
                    }
                    currentIndex++;
                }
            } else {
                if (currentIndex === selectedIndex.value) {
                    // Fallback for results without contexts
                    if (result.url) {
                        navigateToResult(result);
                    }
                    return;
                }
                currentIndex++;
            }
        }
    };

    // Navigate to search result
    const navigateToResult = (result: any) => {
        // Check if company switching is needed
        if (result.company_switch) {
            // First switch company, then navigate to the target URL
            router.patch(
                route('v2.dashboard.selected.set'),
                {
                    selected_company_id: result.company_switch,
                },
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        // After company switch, navigate to the target page
                        router.visit(result.url);
                    },
                },
            );
        } else {
            // Direct navigation
            router.visit(result.url);
        }
        closeDropdown();
        clearSearch();
    };

    // Close dropdown
    const closeDropdown = () => {
        isDropdownOpen.value = false;
        selectedIndex.value = -1;
        if (inputRef.value?.$el && typeof inputRef.value.$el.blur === 'function') {
            inputRef.value.$el.blur();
        }
    };

    // Clear search
    const handleClear = () => {
        clearSearch();
        closeDropdown();
        inputRef.value?.$el?.focus();
    };

    // Click outside handler
    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;
        if (!target.closest('.global-search-container')) {
            closeDropdown();
        }
    };

    // Global keyboard shortcut (Ctrl/Cmd + K)
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            inputRef.value?.$el?.focus();
        }
    };

    // Get type color for badges
    const getTypeColor = (type: string) => {
        const colors = {
            Company: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
            User: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
            Employee: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            Voucher: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
            Navigation: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
        };
        return (
            colors[type as keyof typeof colors] ||
            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
        );
    };

    // Get current selection state for an item
    const getItemSelectionClass = (resultIndex: number, contextIndex: number = 0) => {
        let currentIndex = 0;
        for (let i = 0; i < resultIndex; i++) {
            const result = searchResults.value?.results?.[i];
            currentIndex += result?.contexts?.length || 1;
        }
        currentIndex += contextIndex;

        return currentIndex === selectedIndex.value;
    };

    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleGlobalKeyDown);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleGlobalKeyDown);
    });

    watch(
        [searchResults, hasResults, isLoading, isFetching],
        ([results, hasRes, loading, fetching]) => {
            console.log('Search State:', {
                results,
                hasResults: hasRes,
                isLoading: loading,
                isFetching: fetching,
                searchQuery: searchQuery.value,
                isDropdownOpen: isDropdownOpen.value,
            });
        },
        { deep: true },
    );
</script>

<template>
    <div class="global-search-container relative w-full">
        <!-- Search Input -->
        <div class="flex items-center">
            <Input
                ref="inputRef"
                :model-value="searchQuery"
                :placeholder="t('global-search.placeholder')"
                class="placeholder:text-muted-foreground h-11 w-full !border-none pr-8 pl-9 text-sm !shadow-none"
                @input="handleInput"
                @keydown="handleKeyDown"
                @focus="searchQuery.length >= 2 && (isDropdownOpen = true)"
            />
            <Search class="text-muted-foreground pointer-events-none absolute left-3 size-4" />
            <button
                v-if="searchQuery"
                @click="handleClear"
                class="text-muted-foreground hover:text-foreground absolute right-3 size-4 cursor-pointer"
            >
                <X class="size-4" />
            </button>
        </div>

        <Card
            v-if="isDropdownOpen && (isFetching || hasResults || (isMinimumQuery && !isFetching))"
            class="absolute top-full right-0 left-0 z-50 mt-1 max-h-96 overflow-hidden border shadow-lg"
        >
            <CardContent class="p-2">
                <div
                    v-if="isFetching"
                    class="flex items-center justify-center p-4"
                >
                    <LoaderCircle class="h-4 w-4 animate-spin" />
                    <span class="text-muted-foreground ml-2 text-sm">{{
                        t('global-search.searching')
                    }}</span>
                </div>

                <div
                    v-else-if="hasResults"
                    ref="resultsRef"
                    class="max-h-80 overflow-y-auto"
                >
                    <template
                        v-for="(result, resultIndex) in searchResults?.results || []"
                        :key="`${result.type}-${result.id}`"
                    >
                        <template
                            v-for="(context, contextIndex) in result.contexts"
                            :key="`${result.type}-${result.id}-${contextIndex}`"
                        >
                            <GlobalSearchResult
                                :result="result"
                                :context="context"
                                :context-index="contextIndex"
                                :result-index="resultIndex"
                                :search-query="searchQuery"
                                :is-selected="getItemSelectionClass(resultIndex, contextIndex)"
                                @navigate="navigateToResult"
                            />
                        </template>
                    </template>
                </div>

                <div
                    v-else-if="isMinimumQuery && !isFetching"
                    class="text-muted-foreground p-4 text-center text-sm"
                >
                    {{ t('global-search.no_results') }}
                </div>
            </CardContent>
        </Card>
    </div>
</template>

<style scoped>
    .max-h-80::-webkit-scrollbar {
        width: 6px;
    }

    .max-h-80::-webkit-scrollbar-track {
        background: transparent;
    }

    .max-h-80::-webkit-scrollbar-thumb {
        background: hsl(var(--border));
        border-radius: 3px;
    }

    .max-h-80::-webkit-scrollbar-thumb:hover {
        background: hsl(var(--muted-foreground));
    }

    :deep(mark) {
        background-color: hsl(var(--primary) / 0.2);
        color: hsl(var(--primary));
        padding: 1px 2px;
        border-radius: 2px;
        font-weight: 500;
    }
</style>
